import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  ENTITY_TYPE,
  getEntityTypeFromSuspensionType,
  toTitleCase,
} from './utils';
import Dropdown, { DropdownOption } from './Dropdown';
import { RootState } from '../reducers';
import {
  addCustomerSuspension,
  addSuspension,
  setEntitySuspension,
} from '../actions/orderActions';
import { ReactComponent as SuccessfulDeletion } from '../assets/suspend-success-tick.svg';
import { ReactComponent as Alert } from '../assets/action_history_alert.svg';
import { ReactComponent as CloseGrey } from '../assets/close-grey.svg';
import {
  CustomerSuspensionParams,
  SuspendEntityPayload,
} from '../model/customerSuspensionDetails';
import {
  FormattedMessage,
  injectIntl,
  useIntl,
  WrappedComponentProps,
} from 'react-intl';
import {
  bundleDetailsSelector,
  customerDetailsSelector,
  orderDetailsSelector,
  queryTextSelector,
} from '../selectors/bundleSelectors';
import { getLatestOrderOfOrderType } from '../helpers/product.helpers';

const SUSPENSION_SOURCE = {
  ABUSE_MODEL: 'abuse_model',
  AWS_COMPLAINT: 'aws_complaint',
  ARF: 'arf',
  SPAM: 'spam',
  BOUNCE: 'bounce',
  PHISHING_COMPLAINT: 'phishing_complaint',
  GENERAL_COMPLAINT: 'complaint',
  EMAIL_PHISHING_DETECTED: 'phishing_detected_in_email',
};

const MODEL_FEEDBACK = {
  BRAND_NAME_IN_DOMAIN: 'brand-name-in-domain',
  DOMAIN_PATTERN: 'domain-pattern',
  BRAND_NAME_IN_EMAIL: 'brand-name-in-email',
  GIBBERISH: 'gibberish',
};

interface SuspensionDetailsProps {
  token: string;
  isSuspensionInProgress: boolean;
  isSuspensionSuccess: boolean;
  suspensionError: string;
  accountId: number;
  //   authToken: string,
  //   region: string
  // ) => void;
  customerSuspensionOrderInfo: CustomerSuspensionParams;
  isCustomerSuspended: boolean;
}

interface ParentComponentProps {
  suspensionType: (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE];
  orderType?: string;
  closeModal: (
    entityType: (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE],
    shouldRefetchData: boolean
  ) => void;
}

type Props = ParentComponentProps &
  SuspensionDetailsProps &
  WrappedComponentProps;

const Suspension: React.FC<Props> = (props) => {
  const {
    suspensionType,
    closeModal,
    token,
    orderType,
    isSuspensionInProgress,
    isSuspensionSuccess,
    suspensionError,
    accountId,
    customerSuspensionOrderInfo,
    isCustomerSuspended,
  } = props;

  const dispatch = useDispatch();
  const intl = useIntl();

  // Local state
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [source, setSource] = useState('');
  const [modelFeedback, setModelFeedback] = useState<{
    [key: string]: boolean;
  }>({});

  const customerDetails = useSelector(customerDetailsSelector);
  const orders = useSelector(orderDetailsSelector);
  const queryText = useSelector(queryTextSelector);

  // Timeout for auto-close on success
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (isSuspensionSuccess) {
      timeoutId = setTimeout(() => {
        handleCloseModal();
      }, 1000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isSuspensionSuccess]);

  // Memoized Dropdown options based on intl and suspensionType
  const suspensionReasons = useMemo<DropdownOption[]>(() => {
    const reasons = [
      { label: intl.formatMessage({ id: 'abuse' }), value: 'abuse' },
      {
        label: intl.formatMessage({ id: 'payment_abuse' }),
        value: 'payment_abuse',
      },
      {
        label: intl.formatMessage({ id: 'charge_back' }),
        value: 'charge_back',
      },
      { label: intl.formatMessage({ id: 'expiry' }), value: 'expiry' },
      { label: intl.formatMessage({ id: 'custom-reason' }), value: 'custom' },
    ];
    return suspensionType === ENTITY_TYPE.CUSTOMER
      ? reasons.filter((r) => r.value !== 'expiry')
      : reasons;
  }, [intl, suspensionType]);

  const suspensionSourceOptions = useMemo<DropdownOption[]>(
    () => [
      {
        label: intl.formatMessage({ id: 'abuse-model' }),
        value: SUSPENSION_SOURCE.ABUSE_MODEL,
      },
      {
        label: intl.formatMessage({ id: 'aws-complaint' }),
        value: SUSPENSION_SOURCE.AWS_COMPLAINT,
      },
      {
        label: intl.formatMessage({ id: 'arf' }),
        value: SUSPENSION_SOURCE.ARF,
      },
      {
        label: intl.formatMessage({ id: 'spam' }),
        value: SUSPENSION_SOURCE.SPAM,
      },
      {
        label: intl.formatMessage({ id: 'bounce' }),
        value: SUSPENSION_SOURCE.BOUNCE,
      },
      {
        label: intl.formatMessage({ id: 'phishing-complaint' }),
        value: SUSPENSION_SOURCE.PHISHING_COMPLAINT,
      },
      {
        label: intl.formatMessage({ id: 'general-complaint' }),
        value: SUSPENSION_SOURCE.GENERAL_COMPLAINT,
      },
      {
        label: intl.formatMessage({ id: 'email_phishing_detected' }),
        value: SUSPENSION_SOURCE.EMAIL_PHISHING_DETECTED,
      },
    ],
    [intl]
  );

  const modelFeedbackOptions = useMemo<DropdownOption[]>(
    () => [
      {
        label: intl.formatMessage({ id: 'brand-name-in-domain' }),
        value: MODEL_FEEDBACK.BRAND_NAME_IN_DOMAIN,
      },
      {
        label: intl.formatMessage({ id: 'domain-pattern' }),
        value: MODEL_FEEDBACK.DOMAIN_PATTERN,
      },
      {
        label: intl.formatMessage({ id: 'brand-name-in-email' }),
        value: MODEL_FEEDBACK.BRAND_NAME_IN_EMAIL,
      },
      {
        label: intl.formatMessage({ id: 'gibberish' }),
        value: MODEL_FEEDBACK.GIBBERISH,
      },
    ],
    [intl]
  );

  // Handlers
  const handleSuspensionItemClick = useCallback((item: DropdownOption) => {
    setReason(item.value);
  }, []);

  const handleSourceItemClick = useCallback((item: DropdownOption) => {
    setSource(item.value);
    if (item.value !== SUSPENSION_SOURCE.ABUSE_MODEL) {
      setModelFeedback({});
    }
  }, []);

  const handleModelFeedback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setModelFeedback((prev) => ({
        ...prev,
        [e.target.value]: e.target.checked,
      }));
    },
    []
  );

  const handleNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNote(e.target.value);
    },
    []
  );

  const handleCustomReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomReason(e.target.value);
    },
    []
  );

  const getCustomerOrders = useCallback(() => {
    return customerSuspensionOrderInfo.orderInfo.orders;
  }, [customerSuspensionOrderInfo]);

  const getEntityDetailsToSuspend = () => {
    let entityDetails: { entityId: number; entityType: string } = {
      entityId: 0,
      entityType: '',
    };
    if (suspensionType === ENTITY_TYPE.CUSTOMER) {
      const cid = customerDetails.id;
      entityDetails = {
        entityId: cid,
        entityType: ENTITY_TYPE.CUSTOMER.toUpperCase(),
      };
    } else if (suspensionType === ENTITY_TYPE.ACCOUNT) {
      entityDetails = {
        entityId: accountId,
        entityType: ENTITY_TYPE.ACCOUNT.toUpperCase(),
      };
    } else if (orderType) {
      const order = getLatestOrderOfOrderType(orders, orderType);
      entityDetails = {
        entityId: order.id,
        entityType: ENTITY_TYPE.ORDER.toUpperCase(),
      };
    }

    return entityDetails;
  };

  const handleSubmit = useCallback(() => {
    if (isSuspensionSuccess) {
      handleCloseModal();
      return;
    }
    let computedSuspensionType = reason === 'custom' ? customReason : reason;
    const modelFeedbackString = Object.keys(modelFeedback)
      .filter((k) => modelFeedback[k])
      .join(',');
    const commonProps = {
      suspensionType: computedSuspensionType,
      metadata: {
        note,
        source,
        modelFeedback: modelFeedbackString,
      },
    };

    const suspensionPayload: SuspendEntityPayload = {
      ...getEntityDetailsToSuspend(),
      ...commonProps,
    };
    dispatch(setEntitySuspension(suspensionPayload, 'suspend', token));
  }, [
    reason,
    customReason,
    modelFeedback,
    note,
    source,
    suspensionType,
    token,
    isSuspensionSuccess,
    getCustomerOrders,
  ]);

  const createFooterNote = useCallback(() => {
    return intl.formatMessage(
      { id: 'suspend-footer-note' },
      { entity: getEntityTypeFromSuspensionType(suspensionType) }
    );
  }, [intl, suspensionType]);

  const createFooterNoteForAccount = useCallback(() => {
    switch (suspensionType) {
      case ENTITY_TYPE.ACCOUNT:
        return intl.formatMessage({ id: 'account-not-able-to-send-mail' });
      case ENTITY_TYPE.ORDER:
        return intl.formatMessage({ id: 'accounts-not-able-to-send-mail' });
      case ENTITY_TYPE.CUSTOMER:
        return intl.formatMessage({ id: 'all-accounts-not-able-to-send-mail' });
      default:
        return '';
    }
  }, [intl, suspensionType]);

  const createFooterNoteForBilling = useCallback(() => {
    switch (suspensionType) {
      case ENTITY_TYPE.ACCOUNT:
        return intl.formatMessage({ id: 'footer-note-billing-account' });
      case ENTITY_TYPE.ORDER:
        return intl.formatMessage({ id: 'footer-note-billing-domain' });
      case ENTITY_TYPE.CUSTOMER:
        return intl.formatMessage({ id: 'footer-note-billing-customer' });
      default:
        return '';
    }
  }, [intl, suspensionType]);

  const createSuspensionText = useCallback(() => {
    switch (suspensionType) {
      case ENTITY_TYPE.ACCOUNT:
        return toTitleCase(intl.formatMessage({ id: 'suspend-account' }));
      case ENTITY_TYPE.ORDER:
        return toTitleCase(intl.formatMessage({ id: 'suspend-order' }));
      case ENTITY_TYPE.CUSTOMER:
        return toTitleCase(intl.formatMessage({ id: 'suspend-customer' }));
      default:
        return '';
    }
  }, [intl, suspensionType]);

  const createSuspendingText = useCallback(() => {
    switch (suspensionType) {
      case ENTITY_TYPE.ACCOUNT:
        return toTitleCase(intl.formatMessage({ id: 'suspending-account' }));
      case ENTITY_TYPE.ORDER:
        return toTitleCase(intl.formatMessage({ id: 'suspending-order' }));
      case ENTITY_TYPE.CUSTOMER:
        return toTitleCase(intl.formatMessage({ id: 'suspending-customer' }));
      default:
        return '';
    }
  }, [intl, suspensionType]);

  const getAlertDivForSuspension = useCallback(() => {
    return (
      <div className="alert-container-suspension">
        <div className="alert-container-icon">
          <Alert />
        </div>
        <span className="alert-container-text">
          <FormattedMessage
            id="action-blocked-customer-suspended"
            values={{ entity: getEntityTypeFromSuspensionType(suspensionType) }}
          />
        </span>
      </div>
    );
  }, [suspensionType]);

  const isSuspensionBlocked = useCallback(() => {
    return isCustomerSuspended && suspensionType !== ENTITY_TYPE.CUSTOMER;
  }, [isCustomerSuspended, suspensionType]);

  const { domainName } = useSelector(bundleDetailsSelector);

  // Derived values for rendering
  const modalSubtitle =
    suspensionType === ENTITY_TYPE.ORDER
      ? `@${domainName}`
      : suspensionType === ENTITY_TYPE.ACCOUNT
        ? queryText
        : customerDetails.email;
  const suspensionNoteDefault = intl.formatMessage({
    id: 'suspension-note-default-text',
  });
  const disableSuspendButton =
    !reason ||
    !source ||
    (reason === 'custom' ? !customReason.trim().length : false);
  let suspendButtonText: React.ReactNode = createSuspensionText();
  if (isSuspensionInProgress) {
    suspendButtonText = (
      <div className="modal-button-div">
        <div className="modal-spinner" />
        {createSuspendingText()}
      </div>
    );
  } else if (isSuspensionSuccess) {
    suspendButtonText = (
      <div className="modal-button-div">
        <SuccessfulDeletion className="modal-button-success-icon" />
        <FormattedMessage id="suspended" />
      </div>
    );
  }

  const handleCloseModal = (shoudldRefetch = true) => {
    closeModal(suspensionType, shoudldRefetch);
  };

  return (
    <div>
      <CloseGrey
        className="alert-modal-close-button"
        onClick={() => handleCloseModal(false)}
      />
      <div className="suspension-container">
        <div className="suspension-heading">{createSuspensionText()}</div>
        <div className="suspension-subheading">{modalSubtitle}</div>
        {isSuspensionBlocked() && getAlertDivForSuspension()}
        <div className="suspension-input-heading">
          <FormattedMessage id="reason" />
        </div>
        <Dropdown
          mainClass="suspension-dropdown-container"
          headerClass="suspension-dropdown-header"
          headerTitleClass="suspension-dropdown-header-title"
          listContainerClass="suspension-dropdown-list-container"
          dropDownItemClass="suspension-dropdown-menu-item"
          onItemClick={handleSuspensionItemClick}
          defaultItemSelectedIndexes={null}
          placeholderText={intl.formatMessage({ id: 'suspension-reason' })}
          placeholderClass="suspension-dropdown-header-title-placeholder"
          list={suspensionReasons}
          shouldAllowMultiSelect={false}
        />
        {reason === 'custom' && (
          <input
            className="suspension-custom-reason"
            placeholder={intl.formatMessage({
              id: 'suspension-custom-region-placeholder',
            })}
            onChange={handleCustomReasonChange}
            value={customReason}
          />
        )}
        <div className="suspension-input-heading suspension-note-container">
          <FormattedMessage
            id="enter-internal-note"
            values={{
              span: (word: any) => (
                <span className="suspension-note-extra"> {word} </span>
              ),
            }}
          />
        </div>
        <textarea
          className="suspension-note"
          placeholder={suspensionNoteDefault}
          onChange={handleNoteChange}
          value={note}
        />
        <div className="suspension-input-heading">
          <FormattedMessage id="suspension-source" />
        </div>
        <Dropdown
          mainClass="suspension-dropdown-container"
          headerClass="suspension-dropdown-header"
          headerTitleClass="suspension-dropdown-header-title"
          listContainerClass="suspension-dropdown-list-container"
          dropDownItemClass="suspension-dropdown-menu-item"
          onItemClick={handleSourceItemClick}
          defaultItemSelectedIndexes={null}
          placeholderText={intl.formatMessage({ id: 'select-source' })}
          placeholderClass="suspension-dropdown-header-title-placeholder"
          list={suspensionSourceOptions}
          shouldAllowMultiSelect={false}
        />
        {source === SUSPENSION_SOURCE.ABUSE_MODEL && (
          <div className="inaccuracy-points">
            <FormattedMessage id="inaccuracy-points-label" tagName="label" />
            <ul>
              {modelFeedbackOptions.map((item) => (
                <li key={item.value}>
                  <input
                    onChange={handleModelFeedback}
                    name="inaccuracyReason"
                    type="checkbox"
                    id={item.value}
                    value={item.value}
                    checked={!!modelFeedback[item.value]}
                  />
                  <label htmlFor={item.value}>{item.label}</label>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="suspension-footer">
          {createFooterNote()}
          <ul>
            <li>{createFooterNoteForAccount()}</li>
            <li>{createFooterNoteForBilling()}</li>
          </ul>
        </div>
        <div className="suspension-button-container">
          <button
            className="button button-primary suspension-button"
            disabled={disableSuspendButton || isSuspensionBlocked()}
            onClick={handleSubmit}
          >
            {suspendButtonText}
          </button>
          <button
            className="button suspension-button modal-cancel-button"
            onClick={() => handleCloseModal(false)}
          >
            <FormattedMessage id="cancel" />
          </button>
        </div>
        {suspensionError && (
          <div className="suspension-error">{suspensionError}</div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: state.auth.token,
  isSuspensionInProgress: (state.orders as any).isSuspensionInProgress,
  isSuspensionSuccess: (state.orders as any).isSuspensionSuccess,
  suspensionError: (state.orders as any).suspensionError,
  customerSuspensionOrderInfo: (state.orders as any)
    .customerSuspensionOrderInfo,
  isCustomerSuspended: (state.orders as any).isCustomerSuspendedOrderDetails,
});

const mapDispatchToProps = (dispatch: any) => ({
  ...bindActionCreators({ addSuspension, addCustomerSuspension }, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(Suspension));
